'use client';

import { ProductCard } from '@/components/product-card';
import type { ProductCardProps } from '@/lib/types';
import { serializeProductCardToElevarProduct } from '@/lib/utils';
import { Container } from '@veranda/shared/components/container';
import {
  type FilterOptions,
  ProductListingProductGridWrapper,
  useProductListingFilters,
  useProductListingPageState,
  useProductListingSortState,
} from '@veranda/shared/components/pages/product-listing';
import {
  type ProductListingPageBaseProps,
  useProductListingProducts,
} from '@veranda/shared/components/pages/product-listing/client';
import { useProductGridViewportTracking } from '@veranda/shared/elevar';
import { trackViewedCategoryKlaviyo } from '@veranda/shared/klaviyo/events';
import { cn } from '@veranda/shared/lib';
import { usePathname } from 'next/navigation';
import { useEffect, useTransition } from 'react';

export function ProductGrid({
  slug,
  data,
  filterOptions,
  initialProducts,
}: {
  slug: string;
  data: ProductListingPageBaseProps;
  filterOptions: FilterOptions[];
  initialProducts?: ProductCardProps[];
}) {
  const pathname = usePathname();
  const { page: currentPage } = useProductListingPageState();
  const { sort } = useProductListingSortState(data.sortOrder);
  const [isPending, startTransition] = useTransition();
  const { filterValues } = useProductListingFilters({
    filterOptions,
    startTransition,
  });

  const { data: products = initialProducts, isLoading } =
    useProductListingProducts({
      slug,
      currentPage,
      sort,
      settings: data.settings,
      filterValues,
    });

  // Use shared viewport tracking hook for collections
  useProductGridViewportTracking({
    listName: pathname,
    products,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (products) {
      trackViewedCategoryKlaviyo({
        category: {
          CategoryName: data.title ?? '',
          CategoryID: data.slug ?? '',
          URL: pathname,
        },
      });
    }
  }, [products]);

  return (
    <section
      className={cn((isLoading || isPending) && 'animate-pulse opacity-50')}
    >
      <Container>
        <ProductListingProductGridWrapper>
          {products?.map((product, index) => {
            if (!product) {
              return null;
            }

            const elevarImpression = serializeProductCardToElevarProduct({
              product,
              position: index + 1,
              list: pathname,
            });

            return (
              <ProductCard
                key={product.slug}
                product={product}
                index={index}
                elevarImpression={elevarImpression}
              />
            );
          })}
        </ProductListingProductGridWrapper>
      </Container>
    </section>
  );
}
